import {
  isEmpty,
  map,
  each,
  compact,
  get,
  last,
  zip,
  isMatch,
  sum,
  find,
  has,
  take,
  isPlainObject,
} from "lodash";
import { v4 as uuidv4 } from "uuid";
import { ControllerRenderProps } from "react-hook-form";
import { useMutation } from "react-query";
import axios from "axios";

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import ImagePreview from "../../fields/fileUploadField/ImagePreview";
import FileInput from "../../fields/fileUploadField/FileInput";
import UploadedFile from "../../fields/fileUploadField/UploadedFile";
import SortableUploadedFile from "../../fields/fileUploadField/SortableUploadedFile";
import UploadBar from "../../fields/fileUploadField/UploadBar";
import { FileData } from "../../../../@types/files";
import { fetchApi } from "../../../../helpers/reactQueryApi";
import storageUploadPrefix from "../../../../helpers/storageUploadPrefix";
import classNames from "classnames";

type FileUploadArgs = {
  multiple?: boolean;
  storageDirectory?: string;
  isImage?: boolean;
  selectExisting?: boolean;
  field: ControllerRenderProps<any>;
  accept?: string;
  disabled?: boolean;
  maxFiles?: number;
  sortable?: boolean;
  error?: boolean;
};

type UploadFileArgs = {
  fileData: { clientId: string; file: File; storageDirectory: string };
  setProgress: (data: FileData) => void;
};
async function uploadFile({ fileData, setProgress }: UploadFileArgs) {
  const data = new FormData();
  data.append("file", fileData.file);
  data.append("name", fileData.file.name);
  data.append("type", fileData.file.type);
  data.append("external_id", fileData.clientId);

  const response = await axios.post(
    `${storageUploadPrefix(fileData.storageDirectory)}/${fileData.storageDirectory}`,
    data,
    {
      onUploadProgress: ({ loaded, total }) => {
        if (total) {
          const progress = (loaded * 100) / total;
          setProgress({
            ...fileData.file,
            name: fileData.file.name,
            type: fileData.file.type,
            size: fileData.file.size,
            clientId: fileData.clientId,
            progress,
          });
        }
      },
    },
  );

  return response.data;
}

function FileUpload({
  multiple,
  storageDirectory = "files",
  isImage,
  selectExisting,
  accept,
  field,
  disabled = false,
  maxFiles,
  sortable = false,
  error = false,
}: FileUploadArgs) {
  let selectedFiles = isPlainObject(field.value) ? [field.value] : field.value;
  // Values sent to onChange being reflected in value take some time, so we have to store the real value locally
  // in case setProgress is fired quickly, i.e. when selecting multiple files
  const onChangeLocal = (newValue) => {
    selectedFiles = newValue;
    field.onChange(newValue);
  };

  const { mutate: getFileToken } = useMutation<
    { token: string },
    Error,
    { fileId: string }
  >(
    async ({ fileId }) =>
      fetchApi(`/api/storage/files/${fileId}/token`, {
        method: "GET",
      }),
    {
      onSuccess: (data, params) => {
        setToken(data.token, params.fileId);
      },
    },
  );

  const setProgress = (data: FileData) => {
    const newValue = map(selectedFiles, (f) =>
      isMatch(f, { clientId: data.clientId }) ? { ...f, ...data } : f,
    );

    if (!find(selectedFiles, { clientId: data.clientId })) {
      newValue.push(data);
    }
    onChangeLocal(newValue);
  };

  const setToken = (token: string, fileId: string) => {
    const newValue = map(selectedFiles, (f) =>
      isMatch(f, { id: fileId }) ? { ...f, token } : f,
    );
    onChangeLocal(newValue);
  };

  const { mutate: onUploadFile } = useMutation(
    (fileData: { clientId: string; file: File; storageDirectory: string }) =>
      uploadFile({ fileData, setProgress }),
    {
      onSuccess: (data, variables) => {
        setProgress({
          ...data,
          id: data.id,
          name: variables.file.name,
          size: variables.file.size,
          type: variables.file.type,
          clientId: variables.clientId,
          state: "volatile",
          progress: 100,
        });
      },
      onError: (error: { response: { status: string } }, data) => {
        setProgress({
          ...data.file,
          clientId: data.clientId,
          progress: 0,
          state: "failed",
          status: error.response.status,
        });
      },
    },
  );

  const onRemoveFile = (criteria: { [key: string]: string }) => {
    window?.bridge?.confirm(I18n.t("js.files.file.delete_confirm"), () => {
      const newValue = compact(
        map(selectedFiles, (f) =>
          isMatch(f, criteria) ? { ...f, state: "removed" } : f,
        ),
      );
      onChangeLocal(newValue);
    });
  };

  // Queues given files for uploading.
  // Discards all but the first file if it is non-multiple
  // and displays warning to the user.
  function handleUploadFiles(rawFiles: File[]) {
    let rawFilesToUpload = rawFiles;
    if (maxFiles) {
      // Only files still occupying a slot count toward the limit — a file that
      // was removed (state:"removed") lingers in the value but its slot is free
      // again, so a replacement may be uploaded (#37).
      const existingFiles = (selectedFiles || []).filter(
        (f) => f.state !== "removed",
      );
      rawFilesToUpload = take(rawFiles, maxFiles - existingFiles.length);
    }

    const filesData = map(rawFilesToUpload, (file) => ({
      name: file.name,
      type: file.type,
      extension: last(file.name.split(".")),
      clientId: uuidv4(),
      progress: 0,
      state: "uploading",
    }));

    const newValue = [...(selectedFiles || []), ...filesData];
    onChangeLocal(newValue);

    each(zip(rawFilesToUpload, filesData), (zippedPair) => {
      const [file, fileData] = zippedPair;
      if (file && fileData) {
        onUploadFile({
          clientId: fileData.clientId,
          file,
          storageDirectory,
        });
      }
    });
  }

  const onSelectExistingFile = (selectedExistingFiles: FileData[]) => {
    const newFiles = compact(
      map(selectedExistingFiles, (file) => {
        file.id && getFileToken({ fileId: file.id });
        return find(selectedFiles, { id: file.id }) ? null : file;
      }),
    );
    onChangeLocal([...(selectedFiles || []), ...newFiles]);
  };

  // Files without state that came with the defaultValue through e.g. edit something
  // are treated as published
  const nonRemovedFiles = compact(
    map(selectedFiles, (file) => {
      if (file.state === "removed") return null;
      if (!has(file, "state")) return { ...file, state: "published" };
      return file;
    }),
  );
  const availableFiles = nonRemovedFiles.filter(
    (file) => file.state === "published" || file.state === "volatile",
  );
  const uploadingFiles = nonRemovedFiles.filter(
    (file) => file.state === "volatile" || file.state === "uploading",
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const getSortableId = (file: FileData, index: number): string =>
    file.id || file.clientId || file.external_id || `file-${index}`;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = nonRemovedFiles.findIndex(
      (f, i) => getSortableId(f, i) === active.id,
    );
    const newIndex = nonRemovedFiles.findIndex(
      (f, i) => getSortableId(f, i) === over.id,
    );
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedNonRemoved = arrayMove(nonRemovedFiles, oldIndex, newIndex);
    let nonRemovedIndex = 0;
    const newSelectedFiles = map(selectedFiles, (file) => {
      if (file.state === "removed") return file;
      return reorderedNonRemoved[nonRemovedIndex++];
    });
    onChangeLocal(newSelectedFiles);
  };

  return (
    <>
      <div
        className={classNames("fileUploadField border-box p-3", {
          "bg-neutral/25": !!disabled,
          // render-driven error border (see .fileUploadField.field-error)
          "field-error field-error-live": !!error,
        })}
      >
        <div className="flex items-stretch gap-2 mt-2">
          {isImage && !multiple ? (
            <ImagePreview
              imageId={get(availableFiles, [0, "id"])}
              storageDirectory={storageDirectory}
            />
          ) : null}
          <FileInput
            isImage={isImage}
            acceptedType={
              accept ||
              (storageDirectory === "images" || storageDirectory === "assets"
                ? "image/*"
                : "*")
            }
            storageDirectory={storageDirectory}
            multiple={multiple}
            empty={isEmpty(availableFiles)}
            onSelectFile={(e) => handleUploadFiles(e.target.files)}
            selectExisting={!isImage && selectExisting}
            onSelectExistingFile={onSelectExistingFile}
            onDropFile={({ files }) => handleUploadFiles(files)}
            disabled={
              disabled || !!(maxFiles && nonRemovedFiles.length >= maxFiles)
            }
          />
        </div>
        <div className="upload-states-view flex flex-col gap-px py-px -m-3 mt-4">
          {uploadingFiles.length > 1 && (
            <div className="upload-status">
              <div className="upload-progress">
                <UploadBar
                  progress={
                    sum(map(uploadingFiles, "progress")) / uploadingFiles.length
                  }
                  state="uploading"
                />
              </div>
              <div className="upload-controls">
                <div>
                  <span className="text-sm">
                    {I18n.t("js.files.uploader.total_progress", {
                      completed: uploadingFiles.filter(
                        (f) => f.state === "volatile",
                      ).length,
                      total: uploadingFiles.length,
                    })}
                  </span>
                </div>
              </div>
            </div>
          )}
          {sortable ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={nonRemovedFiles.map((f, i) => getSortableId(f, i))}
                strategy={verticalListSortingStrategy}
              >
                {map(nonRemovedFiles, (file, key) => (
                  <SortableUploadedFile
                    key={`${file.external_id || file.id}-${key}`}
                    id={getSortableId(file, key)}
                    file={file}
                    remove={onRemoveFile}
                    storageDirectory={storageDirectory}
                    isImage={isImage}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            map(nonRemovedFiles, (file, key) => (
              <UploadedFile
                key={`${file.external_id || file.id}-${key}`}
                file={file}
                remove={onRemoveFile}
                storageDirectory={storageDirectory}
                isImage={isImage}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}

export default FileUpload;
